/**
 * PRDSessionCoordinator - State machine-based session lifecycle manager.
 *
 * Replaces complex useEffect hooks with XState machine for predictable state transitions.
 * Handles:
 * - Session initialization (hydration, loading existing, creating new)
 * - SSE event integration (generation progress, completion)
 * - API calls (start session, cancel session)
 * - Error handling and retry logic
 *
 * Exposes state via render prop pattern for UI rendering.
 */

import { JSX, ReactNode, useEffect, useRef } from 'react';
import { addToast } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useMachine } from '@xstate/react';

import { MIN_EXISTING_PRD_LENGTH } from '~/constants/prd';
import { usePRDGeneration } from '~/hooks/usePRDGeneration';
import { prdSessionMachine } from '~/machines/prdSessionMachine';
import type { PRDSessionContext } from '~/machines/prdSessionMachine';
import { getSession } from '~/services/api';
import { useHasHydrated, usePRDStore, useSessionForProject } from '~/stores/prdStore';
import { useSSEStore } from '~/stores/sseStore';
import { validateClassificationData } from '~/utils/classificationValidation';
import { logger } from '~/utils/logger';

import type { PRDSessionState } from '~/types';

export interface PRDSessionCoordinatorProps {
  projectId: string;
  projectName: string;
  projectDetails?: string;
  existingPRD?: string;
  children: (props: PRDSessionCoordinatorRenderProps) => ReactNode;
}

export interface PRDSessionCoordinatorRenderProps {
  // Current state
  state: string; // Machine state name
  context: PRDSessionContext;

  // Derived states for UI rendering
  isLoading: boolean;
  isGenerating: boolean;
  isComplete: boolean;
  hasError: boolean;

  // Actions
  startSession: () => void;
  cancelSession: () => Promise<void>;
  retry: () => void;
  startOver: () => Promise<void>;
}

/**
 * Component that manages PRD session lifecycle using XState machine.
 */
export function PRDSessionCoordinator({
  children,
  existingPRD,
  projectDetails,
  projectId,
  projectName,
}: PRDSessionCoordinatorProps): JSX.Element {
  const queryClient = useQueryClient();
  const hasHydrated = useHasHydrated();
  const activeSessionId = useSessionForProject(projectId);
  const { clearSession, setActiveSession } = usePRDStore();
  const eventsByProject = useSSEStore(state => state.eventsByProject);
  const addActiveJob = useSSEStore(state => state.addActiveJob);
  const removeActiveJob = useSSEStore(state => state.removeActiveJob);
  const clearProjectEvents = useSSEStore(state => state.clearProjectEvents);

  const isMountedRef = useRef(true);

  // API hooks
  const { cancelSession: cancelSessionMutation, startSession: startSessionMutation } =
    usePRDGeneration(projectId);

  // Initialize state machine
  const [state, send] = useMachine(prdSessionMachine, {
    input: {
      projectId,
      projectName,
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return (): void => {
      isMountedRef.current = false;
    };
  }, []);

  // Effect 1: Handle hydration
  useEffect(() => {
    if (!hasHydrated) return;

    const hasExistingPRD = !!(existingPRD && existingPRD.length >= MIN_EXISTING_PRD_LENGTH);

    send({
      type: 'HYDRATION_COMPLETE',
      activeSessionId: activeSessionId || null,
      hasExistingPRD,
    });
  }, [hasHydrated, activeSessionId, existingPRD, send]);

  // Effect 2: Handle project changes
  const prevProjectIdRef = useRef<string>(projectId);
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      logger.info({ component: 'PRDSessionCoordinator' }, 'Project changed - resetting machine', {
        oldProjectId: prevProjectIdRef.current,
        newProjectId: projectId,
      });
      prevProjectIdRef.current = projectId;
      send({
        type: 'PROJECT_CHANGED',
        projectId,
        projectName,
      });
    }
  }, [projectId, projectName, send]);

  // Effect 3: Load existing session when in loadingExisting state
  useEffect(() => {
    if (state.value !== 'loadingExisting') return;
    if (!state.context.activeSessionId) return;

    const sessionId = state.context.activeSessionId;

    logger.info({ component: 'PRDSessionCoordinator' }, 'Loading existing session', { sessionId });

    const loadSession = async (): Promise<void> => {
      try {
        const response = await getSession(sessionId);

        if (!isMountedRef.current) return;

        // Validate classification data
        const classification = validateClassificationData({
          scenario_type: response.scenario_type,
          project_size: response.project_size,
          classification_confidence: response.classification_confidence,
          classification_reasoning: response.classification_reasoning,
        });

        // Update store timestamp
        setActiveSession(projectId, sessionId);

        send({
          type: 'SESSION_LOADED',
          sessionId,
          sessionState: response.session_state,
          completenessScore: response.completeness_score,
          prdPreview: response.prd_preview || '',
          scenarioType: classification.scenarioType,
          projectSize: classification.projectSize,
          classificationConfidence: classification.confidence,
          classificationReasoning: classification.reasoning,
          generationPhase: response.generation_phase || null,
          status: response.status,
        });

        // If resuming generation, add active job
        if (response.generation_phase && response.generation_phase !== 'completed') {
          const prdJobId = `prd-init-${projectId}`;
          clearProjectEvents(projectId);
          addActiveJob(prdJobId, projectId, 30);
        }
      } catch (error) {
        if (!isMountedRef.current) return;

        const errorMessage = error instanceof Error ? error.message : 'Failed to load session';
        send({ type: 'ERROR', error: errorMessage });
        clearSession(projectId);
      }
    };

    void loadSession();
  }, [
    state.value,
    state.context.activeSessionId,
    projectId,
    send,
    setActiveSession,
    clearSession,
    clearProjectEvents,
    addActiveJob,
  ]);

  // Effect 4: Start new session when in startingNew state
  useEffect(() => {
    if (state.value !== 'startingNew') return;
    if (startSessionMutation.isPending) return; // Avoid double-call

    logger.info({ component: 'PRDSessionCoordinator' }, 'Starting new session', {
      projectId,
      projectName,
    });

    // Add active job for SSE connection
    const prdJobId = `prd-init-${projectId}`;
    clearProjectEvents(projectId);
    addActiveJob(prdJobId, projectId, 30);

    const initializeSession = async (): Promise<void> => {
      try {
        const response = await startSessionMutation.mutateAsync({
          project_id: projectId,
          project_title: projectName,
          project_details: projectDetails || undefined,
        });

        if (!isMountedRef.current) {
          removeActiveJob(prdJobId);
          return;
        }

        // Validate classification data
        const classification = validateClassificationData({
          scenario_type: response.scenario_type,
          project_size: response.project_size,
          classification_confidence: response.classification_confidence,
          classification_reasoning: response.classification_reasoning,
        });

        setActiveSession(projectId, response.session_id);

        send({
          type: 'SESSION_STARTED',
          sessionId: response.session_id,
          sessionState: response.session_state,
          completenessScore: response.completeness_score,
          prdPreview: response.prd_preview || '',
          scenarioType: classification.scenarioType,
          projectSize: classification.projectSize,
          classificationConfidence: classification.confidence,
          classificationReasoning: classification.reasoning,
          generationPhase: response.generation_phase || null,
          isResumed: response.is_resumed || false,
        });

        // Handle resume scenarios
        if (response.is_resumed && response.generation_phase !== 'completed') {
          // Keep job active for SSE events - no toast needed, UI shows progress
          logger.info({ component: 'PRDSessionCoordinator' }, 'Resuming PRD generation', {
            sessionId: response.session_id,
            phase: response.generation_phase,
          });
        } else if (response.session_state === 'generating') {
          // New session still generating
          logger.info(
            { component: 'PRDSessionCoordinator' },
            'New session started - generation in progress',
            {
              sessionId: response.session_id,
              generation_phase: response.generation_phase,
            },
          );
        } else {
          // Ready immediately
          removeActiveJob(prdJobId);
        }
      } catch (error: unknown) {
        if (!isMountedRef.current) {
          removeActiveJob(prdJobId);
          return;
        }

        // Handle 409 Conflict
        if (
          error &&
          typeof error === 'object' &&
          'status' in error &&
          error.status === 409 &&
          'details' in error
        ) {
          const conflictData = error.details as {
            message?: string;
            owner_display_name?: string;
            owner_user_id?: string;
          };
          send({
            type: 'ERROR',
            error:
              conflictData.message ||
              `${conflictData.owner_display_name} is generating a PRD for this project.`,
            conflictOwner:
              conflictData.owner_display_name && conflictData.owner_user_id
                ? {
                    displayName: conflictData.owner_display_name,
                    userId: conflictData.owner_user_id,
                  }
                : undefined,
          });
        } else {
          const errorMessage = error instanceof Error ? error.message : 'Failed to start session';
          send({ type: 'ERROR', error: errorMessage });
        }

        removeActiveJob(prdJobId);
      }
    };

    void initializeSession();
  }, [
    state.value,
    projectId,
    projectName,
    projectDetails,
    startSessionMutation,
    send,
    setActiveSession,
    addActiveJob,
    removeActiveJob,
    clearProjectEvents,
  ]);

  // Effect 5: Watch SSE events for generation completion
  useEffect(() => {
    if (state.value !== 'generating') return;
    if (!state.context.sessionId) return;

    const projectEvents = eventsByProject[projectId] || [];
    // Look for prd_assembling_complete event from autonomous PRD generation
    const assemblingCompleteEvent = projectEvents.find(e => e.event === 'prd_assembling_complete');

    if (assemblingCompleteEvent && assemblingCompleteEvent.data) {
      const data = assemblingCompleteEvent.data as {
        completeness_score?: number;
        next_questions?: unknown[];
        updated_prd?: string;
      };

      logger.info(
        { component: 'PRDSessionCoordinator' },
        'PRD assembling complete event received via SSE',
        {
          sessionId: state.context.sessionId,
          completenessScore: data.completeness_score,
          hasQuestions: (data.next_questions?.length ?? 0) > 0,
        },
      );

      // Determine session state based on questions
      const sessionState =
        data.next_questions && data.next_questions.length > 0
          ? 'awaiting_answers'
          : 'ready_for_finalization';

      send({
        type: 'GENERATION_COMPLETE',
        sessionState: sessionState as PRDSessionState,
        completenessScore: data.completeness_score ?? 0,
        prdPreview: data.updated_prd ?? '',
      });

      // Remove active job
      const prdJobId = `prd-init-${projectId}`;
      removeActiveJob(prdJobId);
    }
  }, [state.value, state.context.sessionId, projectId, eventsByProject, send, removeActiveJob]);

  // Effect 6: Invalidate queries on completion
  useEffect(() => {
    const isComplete =
      state.context.sessionState === 'completed' ||
      state.context.sessionState === 'ready_for_finalization';

    if (isComplete) {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    }
  }, [state.context.sessionState, projectId, queryClient]);

  // Actions
  const handleStartSession = (): void => {
    send({ type: 'START_SESSION' });
  };

  const handleCancelSession = async (): Promise<void> => {
    if (!state.context.sessionId) return;

    try {
      await cancelSessionMutation.mutateAsync(state.context.sessionId);
      clearSession(projectId);
      send({ type: 'CANCEL' });
      addToast({ color: 'primary', description: 'PRD generation cancelled' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel session';
      send({ type: 'ERROR', error: errorMessage });
    }
  };

  const handleRetry = (): void => {
    send({ type: 'RETRY' });
  };

  const handleStartOver = async (): Promise<void> => {
    if (!state.context.sessionId) {
      send({ type: 'START_OVER' });
      return;
    }

    try {
      await cancelSessionMutation.mutateAsync(state.context.sessionId);
    } catch (error) {
      logger.error(
        { component: 'PRDSessionCoordinator', error: String(error) },
        'Failed to cancel session during start over',
      );
    }

    clearSession(projectId);
    send({ type: 'START_OVER' });
  };

  // Derived states for UI
  const isLoading =
    state.value === 'hydrating' ||
    state.value === 'loadingExisting' ||
    state.value === 'startingNew';
  const isGenerating = state.value === 'generating';
  const isComplete =
    state.value === 'readyForFinalization' ||
    state.value === 'completed' ||
    state.context.sessionState === 'completed' ||
    state.context.sessionState === 'ready_for_finalization';
  const hasError = state.value === 'error';

  return (
    <>
      {children({
        state: state.value as string,
        context: state.context,
        isLoading,
        isGenerating,
        isComplete,
        hasError,
        startSession: handleStartSession,
        cancelSession: handleCancelSession,
        retry: handleRetry,
        startOver: handleStartOver,
      })}
    </>
  );
}
